import get from 'lodash/get'
import find from 'lodash/find'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Toast, ToastBody, ToastHeader, Modal, ModalHeader, ModalBody } from 'reactstrap'
import useShallowEqualSelector from '../../hooks/useShallowEqualSelector'
import { ApprovalActions } from '../../redux/actions/Approval'
import { AuthActions } from '../../redux/actions/Auth'
import { CalendarActions } from '../../redux/actions/Calendar'
import { DaterangeActions } from '../../redux/actions/DateRange'
import { InsightActions } from '../../redux/actions/Insight'
import { OauthActions } from '../../redux/actions/Oauth'
import { PostActions } from '../../redux/actions/Post'
import { PostCommentActions } from '../../redux/actions/PostComment'
import { State } from '../../redux/reducers'
import { LibraryActions } from 'src/redux/actions/Library'
import { Button } from 'src/stories/atoms/Button'
import { ErrorMessage } from 'src/components/Error/ErrorMessage'

export const actions = [
  { stateName: 'Auth', action: AuthActions },
  { stateName: 'Oauth', action: OauthActions },
  { stateName: 'Insight', action: InsightActions },
  { stateName: 'Approval', action: ApprovalActions },
  { stateName: 'Post', action: PostActions },
  { stateName: 'PostComment', action: PostCommentActions },
  { stateName: 'Daterange', action: DaterangeActions },
  { stateName: 'Calendar', action: CalendarActions },
  { stateName: 'Library', action: LibraryActions }
]

export const getNotificationData = (
  color: string,
  title: string
): { result: 'error' | 'succeeded'; toastTitle: string; toastIcon: string } => {
  return color === 'danger' || color === 'error'
    ? { result: 'error', toastTitle: 'エラー', toastIcon: 'mdi-alert-octagon' }
    : { result: 'succeeded', toastTitle: title || '通知', toastIcon: 'mdi-bell' }
}

type TToastComponent = {
  onClose?: () => void
  show?: boolean
  color: string
  message: string
  stateName: string
  title?: string
}
const ToastComponent: FC<TToastComponent> = (props: TToastComponent) => {
  const { color, message, stateName, title } = props
  const dispatch = useDispatch()
  const [toastShow, setToastShow] = useState(true)
  const [toastData, setToastData] = useState(null)
  const [modalShow, setModalShow] = useState(true)
  const [modalData, setModalData] = useState(null)
  const delayMilliSeconds = 5000
  const setNotifiedState = get(find(actions, ['stateName', stateName]), 'action')
  const { notified, succeeded, error } = useShallowEqualSelector((state: State) => state[stateName]).toObject()

  const setModalDataEmpty = React.useCallback(() => {
    setModalShow(false)
    setModalData(null)
    !notified && setNotifiedState && dispatch(setNotifiedState.setNotified(true))
    error && setNotifiedState && dispatch(setNotifiedState.setError(null))
  }, [dispatch, error, notified, setNotifiedState])

  useEffect(() => {
    const notificationData = getNotificationData(color, title)
    if (notificationData.result === 'succeeded') {
      setToastData(notificationData)
      setTimeout(() => {
        !notified && setNotifiedState && dispatch(setNotifiedState.setNotified(true))
        succeeded && setNotifiedState && dispatch(setNotifiedState.setSucceeded(false))
        setToastData(null)
      }, delayMilliSeconds)
    }
    if (notificationData.result === 'error') {
      setModalData(notificationData)
    }
  }, [stateName, notified, error, color, title, setNotifiedState, dispatch, succeeded])

  return (
    <>
      {toastData && (
        <Toast
          onClose={() => setToastShow(false)}
          isOpen={toastShow}
          delay={delayMilliSeconds}
          className={`toast-${color}`}
        >
          <ToastHeader className={`toast-${color}-header`} toggle={() => setToastShow(false)}>
            <i className={`mdi ${toastData.toastIcon} rounded mr-2 font-weight-bold`} />
            <strong className={'mr-auto'}>{toastData.toastTitle}</strong>
          </ToastHeader>
          <ToastBody className={'text-dark'} id="toast-message">
            {message}
          </ToastBody>
        </Toast>
      )}
      {modalData && (
        <Modal onClose={setModalDataEmpty} isOpen={modalShow}>
          <ModalHeader className={'bg-danger text-white'} toggle={setModalDataEmpty}>
            <i className={`mdi ${modalData.toastIcon} rounded mr-2 font-weight-bold`} />
            <strong className={'mr-auto'}>{modalData.toastTitle}</strong>
          </ModalHeader>
          <ModalBody className={'text-dark'} id="toast-message">
            <ErrorMessage notification={{ result: 'error', message: error?.message, status: error?.status }} />
            <Button onClick={setModalDataEmpty} className="d-block mx-auto mt-2">
              閉じる
            </Button>
          </ModalBody>
        </Modal>
      )}
    </>
  )
}

export default ToastComponent
